<script lang="ts">
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { beforeUpdate } from 'svelte';
	import ButtonType from './ButtonType.svelte';
	import ButtonModeOptions from './ButtonModeOptions.svelte';
	import InitialStateOptions from './InitialStateOptions.svelte';
	import ButtonTypeInputsReader from './ButtonTypeInputsReader.svelte';
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		real_id,
	} from '../../../../../helpers/interface_helpers';
	import { gen2Request } from '../../../../../services/gen_2_http_reqs';
	import type { SettingRequest } from '../../../../../types/requests/settings/setting';
	import { Button, Container, toast } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { DEVICES_WITH_CYCLE_INPUT_MODE } from 'src/constants';
	import { compare2Versions } from 'src/helpers/firmware_compare_helpers';

	export let device;

	const id = real_id(device.id);

	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore;
	let typeValue = settings[`input:${device.channel}`].type;
	let cacheTypeValue = typeValue;
	let modeValue =
		device.category !== 'inputs_reader'
			? settings[`${get_gen_2_component_channel_structure(device)}`].in_mode
			: '';
	let cacheModeValue = modeValue;
	let initialStateValue =
		device.category !== 'inputs_reader'
			? settings[`${get_gen_2_component_channel_structure(device)}`].initial_state
			: '';
	let cacheInitialStateValue = initialStateValue;
	$: reactiveStateValue = initialStateValue;

	const firmWareVersion =
		$settingsStore?.sys?.device.fw_id || $settingsStore?.DeviceInfo?.fw_id;
	const mandatoryUpdate = compare2Versions(firmWareVersion, '1.3.3') === 1;
	const options = ['button', 'switch'];
	const _options = [
		$format('working_type_button', { default: 'Button' }),
		$format('working_type_switch', { default: 'Switch' }),
	];
	const buttonModeOptions = {
		follow: {
			type: 'switch',
			text: $format('btn_mode_follow_description', {
				default:
					'Set Shelly device to be "Toggle" switch. Act as a flip switch with one state for "ON" and one state for "OFF".',
			}),
			title: $format('btn_mode_follow_title', { default: 'Toggle switch' }),
			description: $format('btn_mode_follow_desc', {
				default: 'Act as a flip switch with one state for "ON" and one state for "OFF".',
			}),
		},
		momentary: {
			type: 'button',
			text: $format('btn_mode_momentary_description', {
				default: 'Set Shelly device to be "Momentary" switch. Push for "ON" or Push for "OFF".',
			}),
			title: $format('btn_mode_momentary_title', { default: 'Momentary switch' }),
			description: $format('btn_mode_momentary_desc', {
				default: 'Push for "ON" or push for "OFF".',
			}),
		},
		cycle: DEVICES_WITH_CYCLE_INPUT_MODE.includes(device?.type)
			? {
					type: 'button',
					text: $format('btn_mode_cycle_description', {
						default:
							'Set Shelly device to be "Cycle" switch. At the push of a input, the sequence is: Ch1-ON Ch2-OFF, Ch1-OFF Ch2-ON, Ch1 & Ch2 ON, Ch1 & Ch2 OFF',
					}),
					title: $format('btn_mode_cycle_title', { default: 'Cycle switch' }),
					description: $format('btn_mode_cycle_desc', {
						default:
							'At the push of an input, the sequence is: Ch1-ON Ch2-OFF, Ch1-OFF Ch2-ON, Ch1 & Ch2 ON, Ch1 & Ch2 OFF.',
					}),
				}
			: undefined,
		flip: {
			type: 'switch',
			text: $format('btn_mode_flip_description', {
				default: 'Set Shelly device to be "Edge" switch. Changes state on every hit.',
			}),
			title: $format('btn_mode_flip_title', { default: 'Edge switch' }),
			description: $format('btn_mode_flip_desc', {
				default: 'Changes state on every hit.',
			}),
		},
		detached: {
			type: 'any',
			text: $format('btn_mode_detached_description', {
				default:
					'Set Shelly device to be in "Detached" switch mode - switch is separated from the relays.',
			}),
			title: $format('btn_mode_detached_title', { default: 'Detached switch' }),
			description: $format('btn_mode_detached_desc', {
				default: 'Switch is separated from the relays.',
			}),
		},
		activate: {
			type: 'any',
			text: $format('btn_mode_activate_description', {
				default:
					'Activation switch - Used with motion sensor. Any input turns ON and resets Auto Off timer',
			}),
			title: $format('btn_mode_activate_title', { default: 'Activation switch' }),
			description: $format('btn_mode_activate_desc', {
				default: 'Used with motion sensor. Any input turns ON and resets Auto Off timer.',
			}),
		},
	};
	const initialStateOptions = {
		on: {
			type: 'any',
			text: $format('init_state_on_description', {
				default: 'Configure Shelly device to Turn ON, when it has power.',
			}),
			title: $format('init_state_on_title', { default: 'Turn on' }),
			description: $format('init_state_on_desc', {
				default: 'Automatically turns the device ON when power is restored.',
			}),
		},
		off: {
			type: 'any',
			text: $format('init_state_off_description', {
				default: 'Configure Shelly device to Turn OFF, when it has power.',
			}),
			title: $format('init_state_off_title', { default: 'Turn off' }),
			description: $format('init_state_off_desc', {
				default: 'Keeps the device OFF when power is restored.',
			}),
		},
		restore_last: {
			type: 'any',
			text: $format('init_state_report_last_description', {
				default:
					'Configure Shelly device to Restore the last mode it was in, when it has power.',
			}),
			title: $format('init_state_restore_last_title', { default: 'Restore last state' }),
			description: $format('init_state_restore_last_desc', {
				default: 'Returns the device to its previous state when power is restored.',
			}),
		},
		match_input: {
			type: 'switch',
			text: $format('init_state_match_input_description', {
				default:
					'Operates the Shelly device according to the state of the switch or button connected to the device.',
			}),
			title: $format('init_state_match_input_title', { default: 'Match input' }),
			description: $format('init_state_match_input_desc', {
				default:
					'Operates the device according to the state of the switch or button connected to the device.',
			}),
		},
	};

	let loading = false;

	const setTypeValue = async () => {
		const res_input_mode: SettingRequest = await gen2Request(device, 'Input.SetConfig', {
			id: device.channel,
			config: { type: typeValue },
		});

		if (res_input_mode.isok) {
			toast.open({
				type: 'success',
				title: $format('input_type_scc_toast', {
					values: { mode_value: _options[options.indexOf(typeValue)] },
					default: 'Button type set to {mode_value}.',
				}),
			});
			cacheTypeValue = typeValue;
		} else {
			typeValue = cacheTypeValue;
			toast.open({
				type: 'danger',
				title: $format('btn_type_err_toast', {
					default: 'Error! Could not change button type.',
				}),
			});
		}
	};

	beforeUpdate(() => {
		Object.keys(buttonModeOptions).forEach((key) => {
			if (typeof buttonModeOptions[key] === 'undefined') {
				delete buttonModeOptions[key];
			}
		});
	});

	const setModeAndInitialOptions = async () => {
		const res: SettingRequest = await gen2Request(
			device,
			`${get_gen_2_component_from_category(device)}.SetConfig`,
			{
				id: device.channel,
				config: {
					in_mode: modeValue,
					initial_state: initialStateValue,
				},
			},
		);

		if (res.isok) {
			if (cacheModeValue !== modeValue) {
				toast.open({
					type: 'success',
					title: $format('btn_mode_scc_toast', {
						values: { mode_value: modeValue },
						default: 'Button mode set to {mode_value}.',
					}),
				});
				cacheModeValue = modeValue;
				settings[`${get_gen_2_component_channel_structure(device)}`].in_mode = modeValue;
				devices_settings.update_devices_settings(id, settings);
			}
			if (cacheInitialStateValue !== initialStateValue) {
				toast.open({
					type: 'success',
					title: $format('init_state_scc_toast', {
						values: { initial_state_value: $format(initialStateValue) },
						default: 'Initial state set to {initial_state_value}.',
					}),
				});
				cacheInitialStateValue = initialStateValue;
				settings[`${get_gen_2_component_channel_structure(device)}`].initial_state =
					initialStateValue;
				devices_settings.update_devices_settings(id, settings);
			}
		} else {
			if (cacheModeValue !== modeValue) {
				modeValue = cacheModeValue;
				toast.open({
					type: 'danger',
					title: $format('btn_mode_err_toast', {
						default: 'Error! Could not change button mode.',
					}),
				});
			}
			if (cacheInitialStateValue !== initialStateValue) {
				initialStateValue = cacheInitialStateValue;
				toast.open({
					type: 'danger',
					title: $format('init_state_err_toast', {
						default: 'Could not change input initial state.',
					}),
				});
			}
		}
	};
	const setButtonTypeInputs = async () => {
		const method = 'input.setconfig';
		const params = { id: device.channel, config: { type: typeValue } };

		const res: SettingRequest = await gen2Request(device, method, params);

		if (res.isok) {
			settings[`input:${device.channel}`].type = typeValue;
			devices_settings.update_devices_settings(id, settings);

			toast.open({
				type: 'success',
				title: $format('btn_type_scc_toast', {
					values: { type_value: typeValue },
					default: 'Button type set to {type_value}.',
				}),
			});
			cacheTypeValue = typeValue;
		} else {
			typeValue = cacheTypeValue;
			toast.open({
				type: 'danger',
				title: $format('btn_type_err_toast', {
					default: 'Error! Failed to set button type.',
				}),
			});
		}
	};

	const sentRequests = async () => {
		if (device.category !== 'inputs_reader') {
			loading = true;
			if (cacheTypeValue !== typeValue) {
				await setTypeValue();
			}
			if (cacheInitialStateValue !== initialStateValue || cacheModeValue !== modeValue) {
				await setModeAndInitialOptions();
			}

			loading = false;
		} else {
			loading = true;
			if (cacheTypeValue !== typeValue) {
				await setButtonTypeInputs();
			}
			loading = false;
		}
	};
</script>

{#if $showNewLayout}
	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		{#if (device.category === 'module' && device.mode === 'inputs_reader') || device.category === 'inputs_reader'}
			<ButtonTypeInputsReader bind:typeValue />
		{:else}
			<ButtonType bind:typeValue {options} {_options} />
			<hr class="mb-m mt-s" />
			<ButtonModeOptions
				{typeValue}
				bind:modeValue
				{buttonModeOptions}
				_modeValue={modeValue}
				bind:reactiveStateValue
			/>
			<InitialStateOptions
				{typeValue}
				bind:initialStateValue
				{mandatoryUpdate}
				{initialStateOptions}
				{modeValue}
			/>
		{/if}
	</Container>
	<div class="mb-s">
		<Button class="mt-l" {loading} size="m" width="full" on:click={sentRequests}>
			{$format('button_save', { default: 'Save' })}
		</Button>
	</div>
{:else}
	{#if (device.category === 'module' && device.mode === 'inputs_reader') || device.category === 'inputs_reader'}
		<ButtonTypeInputsReader bind:typeValue />
	{:else}
		<ButtonType bind:typeValue {options} {_options} />
		<hr class="mb-m mt-s" />
		<ButtonModeOptions
			{typeValue}
			bind:modeValue
			{buttonModeOptions}
			_modeValue={modeValue}
			bind:reactiveStateValue
		/>
		<InitialStateOptions
			{typeValue}
			bind:initialStateValue
			{mandatoryUpdate}
			{initialStateOptions}
			{modeValue}
		/>
	{/if}
	<Button class="mt-s" {loading} size="m" width="full" on:click={sentRequests}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
